const MD_BREAKPOINT = 744;
const DURATION = 5000; // 5 seconds in ms

export default function initBoxTab() {
  const boxTabs = document.querySelectorAll(".box-tab");

  if (!boxTabs.length) return;

  boxTabs.forEach((boxTab) => {
    const nav = boxTab.querySelector(".box-tab-nav");
    const toggle = nav?.querySelector(".box-tab-nav-toggle");
    const toggleText = toggle?.querySelector(".box-tab-nav-toggle-text");
    const navList = nav?.querySelector(".box-tab-nav-list");
    const items = boxTab.querySelectorAll(".box-tab-item");
    const panels = boxTab.querySelectorAll(".box-tab-panel");
    const content = boxTab.querySelector(".box-tab-content");

    if (!items.length || !panels.length) return;

    let currentIndex = 0;
    let isPaused = false;
    let isDesktop = false;
    let progress = 0;
    let lastTime = 0;
    let animationId = null;
    const noAutoplay = boxTab.classList.contains("no-transition");
    const mediaQuery = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`);

    // Update --progress CSS variable on active item (as percentage string)
    function updateProgressUI() {
      const activeItem = items[currentIndex];
      if (activeItem) {
        const percent = Math.round(progress * 100);
        activeItem.style.setProperty("--progress", `${percent}%`);
      }
    }

    // Animation loop using requestAnimationFrame
    function tick(timestamp) {
      if (!lastTime) lastTime = timestamp;

      if (!isPaused && isDesktop && !noAutoplay) {
        const delta = timestamp - lastTime;
        progress += delta / DURATION;

        if (progress >= 1) {
          // Progress complete, go to next tab
          progress = 0;
          lastTime = timestamp;
          nextTab();
          return; // nextTab will start new animation
        }

        updateProgressUI();
      }

      lastTime = timestamp;
      animationId = requestAnimationFrame(tick);
    }

    // Start progress animation
    function startProgress() {
      if (animationId) cancelAnimationFrame(animationId);
      progress = 0;
      lastTime = 0;
      updateProgressUI();
      animationId = requestAnimationFrame(tick);
    }

    // Stop progress animation
    function stopProgress() {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    }

    // Update toggle text with active item title
    function updateToggleText() {
      if (!toggleText) return;
      const activeItem = boxTab.querySelector(".box-tab-item.is-active");
      const title = activeItem?.querySelector(".box-tab-item-title");
      if (title) {
        toggleText.textContent = title.textContent;
      }
    }

    // Switch to specific tab
    function switchTab(index, enableAutoplay = true) {
      // Reset progress on all items immediately
      // CSS hides progress bar on non-active items, so no visible "jump"
      items.forEach((item) => {
        item.style.setProperty("--progress", "0%");
        item.classList.remove("is-active", "is-paused");
      });
      panels.forEach((panel) => panel.classList.remove("is-active"));

      // Set new active
      currentIndex = index;
      progress = 0;
      items[currentIndex].classList.add("is-active");
      panels[currentIndex].classList.add("is-active");

      // Handle autoplay
      if (isDesktop && enableAutoplay && !noAutoplay) {
        startProgress();
        if (isPaused) {
          items[currentIndex].classList.add("is-paused");
        }
      } else {
        stopProgress();
        items[currentIndex].classList.add("is-paused");
      }

      // Update toggle text for mobile
      updateToggleText();

      // Close dropdown on mobile after selection
      if (!isDesktop) {
        closeDropdown();
      }
    }

    // Go to next tab (for auto-advance)
    function nextTab() {
      const next = (currentIndex + 1) % items.length;
      switchTab(next);
    }

    // Pause auto-advance (desktop only, if autoplay enabled)
    function pause() {
      if (!isDesktop || noAutoplay) return;
      isPaused = true;
      items[currentIndex].classList.add("is-paused");
    }

    // Resume auto-advance (desktop only, if autoplay enabled)
    function resume() {
      if (!isDesktop || noAutoplay) return;
      isPaused = false;
      items[currentIndex].classList.remove("is-paused");
    }

    // Toggle dropdown (mobile)
    function toggleDropdown() {
      toggle?.classList.toggle("is-open");
      navList?.classList.toggle("is-open");
    }

    // Close dropdown (mobile)
    function closeDropdown() {
      toggle?.classList.remove("is-open");
      navList?.classList.remove("is-open");
    }

    // Click handlers for tab items
    items.forEach((item, index) => {
      item.addEventListener("click", () => {
        if (index !== currentIndex) {
          switchTab(index);
        } else if (!mediaQuery.matches) {
          // On mobile, clicking active item just closes dropdown
          closeDropdown();
        }
      });
    });

    // Toggle dropdown on mobile
    if (toggle) {
      toggle.addEventListener("click", toggleDropdown);
    }

    // Pause on hover over content
    if (content) {
      content.addEventListener("mouseenter", pause);
      content.addEventListener("mouseleave", resume);
    }

    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      if (!mediaQuery.matches && !nav?.contains(e.target)) {
        closeDropdown();
      }
    });

    // Set min-height on content to tallest panel to prevent layout shift
    function syncContentHeight() {
      if (!content) return;
      content.style.minHeight = "";
      let maxHeight = 0;
      panels.forEach((panel) => {
        // Temporarily make all panels relative to measure
        const wasActive = panel.classList.contains("is-active");
        if (!wasActive) {
          panel.style.position = "relative";
          panel.style.visibility = "hidden";
          panel.style.opacity = "0";
        }
        maxHeight = Math.max(maxHeight, panel.offsetHeight);
        if (!wasActive) {
          panel.style.position = "";
          panel.style.visibility = "";
          panel.style.opacity = "";
        }
      });
      content.style.minHeight = `${maxHeight}px`;
    }

    // Handle breakpoint changes
    function handleBreakpointChange() {
      const wasDesktop = isDesktop;
      isDesktop = mediaQuery.matches;

      closeDropdown();

      // Re-apply current tab with correct autoplay state
      if (wasDesktop !== isDesktop) {
        switchTab(currentIndex, isDesktop && !noAutoplay);
      }
    }

    mediaQuery.addEventListener("change", handleBreakpointChange);

    // Initialize
    isDesktop = mediaQuery.matches;
    switchTab(0, isDesktop && !noAutoplay);
    syncContentHeight();

    window.addEventListener("resize", syncContentHeight);
  });
}
